import React from "react";
import { FormControl, FormControlLabel, FormLabel, RadioGroup, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { RenderHTML } from "components";

import { ErrorMessage, RadioField, StyledRadio } from "../../shared/styled-components";
import { useBooleanQuestionStyles } from "./style";
import { ErrorMessages } from "utils/constants";

interface BooleanQuestionProps {
  questionId: string;
  questionText: string;
  questionHint?: string;
  isMandatory: boolean;
  value: string;
  showMandatoryError: boolean;
  onChange: (questionId: string, value: string) => void;
}

export const BooleanQuestion: React.FC<BooleanQuestionProps> = ({
  questionId,
  questionText,
  questionHint,
  isMandatory,
  value,
  showMandatoryError,
  onChange,
}) => {
  const { t } = useTranslation();
  const classes = useBooleanQuestionStyles();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(questionId, event.target.value);
  };

  const radioGroupId = `boolean-${questionId}`;
  const errorId = `${radioGroupId}-error`;

  return (
    <div className={classes.container}>
      <RadioField>
        <FormControl
          component="fieldset"
          aria-disabled="false"
          error={showMandatoryError && isMandatory}
        >
          <FormLabel
            component="legend"
            htmlFor={radioGroupId}
            className={classes.formLabel}
            aria-label={
              isMandatory ? `${questionText} ${ErrorMessages.InputRequired}` : questionText
            }
          >
            <div className={classes.questionText}>
              <RenderHTML content={questionText ?? ""} />
              {isMandatory && <span className={classes.requiredAsterisk}>*</span>}
            </div>
          </FormLabel>
          <RadioGroup
            id={radioGroupId}
            name={`question-${questionId}`}
            value={value}
            onChange={handleChange}
            className={`${classes.optionButtons} ${
              showMandatoryError && isMandatory ? classes.radioGroupError : ""
            }`}
            aria-describedby={showMandatoryError && isMandatory ? errorId : undefined}
          >
            <FormControlLabel
              value="true"
              aria-label={t("yes")}
              control={<StyledRadio className={classes.hiddenRadio} />}
              className={`${classes.optionControl} ${classes.leftOption} ${
                value === "true" ? classes.optionControlSelected : ""
              }`}
              label={<div className={classes.optionLabel}>{t("yes")}</div>}
            />
            <FormControlLabel
              value="false"
              aria-label={t("no")}
              control={<StyledRadio className={classes.hiddenRadio} />}
              className={`${classes.optionControl} ${classes.rightOption} ${
                value === "false" ? classes.optionControlSelected : ""
              }`}
              label={<div className={classes.optionLabel}>{t("no")}</div>}
            />
          </RadioGroup>
        </FormControl>
      </RadioField>

      {questionHint && <Typography className={classes.hint}>{questionHint}</Typography>}

      {(isMandatory && (!value || value.trim() === "")) || (showMandatoryError && isMandatory) ? (
        <ErrorMessage id={errorId}>{`${questionText} ${ErrorMessages.InputRequired}`}</ErrorMessage>
      ) : null}
    </div>
  );
};
